import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Check from '@material-ui/icons/Check';
import Clear from '@material-ui/icons/Clear';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import {useTranslation} from 'react-i18next';
import {useApiNotification} from "../ui/ApiNotificationProvider";
import {OrganizationUnit} from "../../models/organization";
import {ConfirmationPrompt} from "../ui/ConfirmationPrompt";
import {renameOrganizationUnit} from "../../api/organization-unit-functions";

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex'
    },
    form: {
        display: 'flex',
        alignItems: 'center'
    },
    input: {
        flexGrow: 1,
        margin: '5px 20px'
    },
    cancelBtn: {
        color: theme.palette.error.main
    },
    confirmBtn: {
        color: theme.palette.success.main
    }
}));

interface OrganizationUnitTreeEditProps {
    unit: OrganizationUnit;
    onEdited: (updatedUnit: OrganizationUnit) => void;
    onCancel: () => void;
}

const OrganizationUnitTreeEdit: React.FC<OrganizationUnitTreeEditProps> = ({unit, onEdited, onCancel}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const [name, setName] = useState(unit.name);
    const [error, setError] = useState(false);
    const {showSuccess, handleError} = useApiNotification();
    const [requireConfirmation, setRequireConfirmation] = useState(false);

    const nameChangeHandler = (e: any) => {
        if (name && error)
            setError(false);
        setName(e.target.value);
    };

    const cancelEditHandler = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setName(unit.name);
        setError(false);
        onCancel();
    };

    const saveEditHandler = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (!name)
            return setError(true);

        if(unit.level === 0) {
            setRequireConfirmation(true);
        } else {
            editOrgUnit(name);
        }
    };

    const editOrgUnit = (orgUnitName: any) => {
        if (!orgUnitName)
            return setError(true);

        renameOrganizationUnit(unit, orgUnitName)
            .then(orgUnit => {
                setName(orgUnit.name);
                showSuccess();
                onEdited(orgUnit);
            })
            .catch(handleError);
    };

    return <>
        <ConfirmationPrompt
            title={t(`OrganizationUnits.RenameConfirmation.Title`)}
            description={t('OrganizationUnits.RenameConfirmation.Description')}
            open={requireConfirmation}
            confirmationButtonKey={t('_Common.Labels.Edit')}
            onCancel={(e: any) => {
                setRequireConfirmation(false);
                cancelEditHandler(e);
            }}
            onConfirm={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
                editOrgUnit(name);
            }}
        />
        <form className={classes.form} noValidate autoComplete="off" onSubmit={(e)=> saveEditHandler(e)}>
            <FormControl className={classes.input} error={error} size="small">
                <InputLabel htmlFor="org-unit-name">
                    {error ? `${t('_Common.Labels.Name')} ${t('_Common.Messages.IsRequired')}` :
                        t('_Common.Labels.Name')}
                </InputLabel>
                <Input id="org-unit-name" aria-describedby="org-unit-name"
                       onChange={nameChangeHandler}
                       onClick={e => e.stopPropagation()}
                       value={name}
                    // eslint-disable-line react/jsx-tag-spacing
                    // @ts-ignore
                       variant="outlined"
                />
            </FormControl>

            <div className={(classes as any).action}>
                <IconButton aria-label="save" className={classes.confirmBtn} onClick={saveEditHandler}>
                    <Check/>
                </IconButton>
                <IconButton aria-label="cancel" className={classes.cancelBtn} onClick={cancelEditHandler}>
                    <Clear/>
                </IconButton>
            </div>
    </form>
        </>
};
export default OrganizationUnitTreeEdit;
